import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, FormControlLabel, Switch, Divider, ToggleButton, ToggleButtonGroup,
    FormControl, InputLabel, Select, MenuItem, TextField, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { InstitutionService } from '../../services/institution.service';
import { InstitutionSettingsService } from '../../services/institutionSettings.service';
import { IInstitution, NoShiftPunchPolicy } from '../../interfaces/institution.interface';
import { WeekDay } from '../../interfaces/institutionSettings.interface';
import { useToast } from '../../context/ToastContext';
import { helpWhy } from '../../help/why';

const WEEK_DAYS: { value: WeekDay; label: string }[] = [
    { value: 'MONDAY', label: 'Mon' },
    { value: 'TUESDAY', label: 'Tue' },
    { value: 'WEDNESDAY', label: 'Wed' },
    { value: 'THURSDAY', label: 'Thu' },
    { value: 'FRIDAY', label: 'Fri' },
    { value: 'SATURDAY', label: 'Sat' },
    { value: 'SUNDAY', label: 'Sun' },
];

const InstitutionDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    const [institution, setInstitution] = useState<IInstitution | null>(null);
    const [isAttendanceButtonEnabled, setIsAttendanceButtonEnabled] = useState(true);
    const [weekendDays, setWeekendDays] = useState<WeekDay[]>(['FRIDAY', 'SATURDAY']);
    const [noShiftPunchPolicy, setNoShiftPunchPolicy] = useState<NoShiftPunchPolicy>('LEGACY_ROW');
    const [address, setAddress] = useState('');
    const [tinBin, setTinBin] = useState('');
    const [name, setName] = useState('');
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [removeLogoConfirmOpen, setRemoveLogoConfirmOpen] = useState(false);
    const [removingLogo, setRemovingLogo] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const logoInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (id) {
            loadData();
        }
    }, [id]);

    const loadData = async () => {
        try {
            setLoading(true);
            const inst = await InstitutionService.getInstitutionById(id!);
            setInstitution(inst);
            setNoShiftPunchPolicy(inst.noShiftPunchPolicy ?? 'LEGACY_ROW');
            setAddress(inst.address ?? '');
            setTinBin(inst.tinBin ?? '');
            setName(inst.name ?? '');

            const settings = await InstitutionSettingsService.getSettings(id!);
            if (settings) {
                setIsAttendanceButtonEnabled(settings.isAttendanceButtonEnabled);
                if (settings.weekendDays && settings.weekendDays.length > 0) {
                    setWeekendDays(settings.weekendDays);
                }
            }
        } catch (error) {
            toast.showApiError(error, 'Failed to load institution details');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveSettings = async () => {
        if (weekendDays.length === 0) {
            toast.showError('Select at least one weekend day');
            return;
        }
        if (weekendDays.length >= 7) {
            toast.showError('Weekend cannot cover all 7 days');
            return;
        }
        if (!name.trim()) {
            toast.showError('Institution name is required');
            return;
        }
        try {
            setSaving(true);
            if (institution) {
                await InstitutionService.updateInstitution(id!, {
                    name: name.trim(),
                    code: institution.code,
                    description: institution.description,
                    email: institution.email,
                    fiscalYearStartMonth: institution.fiscalYearStartMonth,
                    noShiftPunchPolicy,
                    address,
                    tinBin,
                });
            }
            await InstitutionSettingsService.updateSettings(id!, { isAttendanceButtonEnabled });
            await InstitutionSettingsService.updateWeekendDays(id!, weekendDays);
            setInstitution({ ...institution!, name: name.trim() });
            toast.showSuccess('Institution settings saved');
        } catch (error) {
            toast.showApiError(error, 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleUploadLogo = async () => {
        if (!logoFile) {
            toast.showError('Choose a logo image first');
            return;
        }
        try {
            setUploadingLogo(true);
            await InstitutionService.uploadLogo(id!, logoFile);
            toast.showSuccess('Logo uploaded');
            setLogoFile(null);
        } catch (error) {
            toast.showApiError(error, 'Failed to upload logo');
        } finally {
            setUploadingLogo(false);
            if (logoInputRef.current) {
                logoInputRef.current.value = '';
            }
        }
    };

    const handleRemoveLogo = async () => {
        try {
            setRemovingLogo(true);
            await InstitutionService.deleteLogo(id!);
            toast.showSuccess('Logo removed');
        } catch (error) {
            toast.showApiError(error, 'Failed to remove logo');
        } finally {
            setRemovingLogo(false);
            setRemoveLogoConfirmOpen(false);
        }
    };

    if (loading) {
        return <Box sx={{ p: 3 }}><Typography>Loading...</Typography></Box>;
    }

    if (!institution) {
        return <Box sx={{ p: 3 }}><Typography>Institution not found</Typography></Box>;
    }

    return (
        <Box sx={{ p: 3 }} data-help-page="institutions.detail.page">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Button startIcon={<ArrowBack />} onClick={() => navigate('/institutions')} data-help="institutions.detail.back">
                    Back
                </Button>
                <Typography variant="h5">{institution.name}</Typography>
            </Box>

            <Paper sx={{ p: 3, mb: 3 }} data-help-section="institutions.detail.section.info">
                <Typography variant="h6" sx={{ mb: 2 }}>Institution Info</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                        {/* Saved with the settings below; it prints on payslips, letters and registers. */}
                        <TextField
                            label="Name"
                            data-help="institutions.detail.field.name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            size="small"
                            fullWidth
                            required
                        />
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">Code</Typography>
                        <Typography>{institution.code}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">Description</Typography>
                        <Typography>{institution.description || '-'}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">Admin Email</Typography>
                        <Typography>{institution.email || '-'}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">Fiscal Year Start Month</Typography>
                        <Typography>{institution.fiscalYearStartMonth}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">Status</Typography>
                        <Typography>
                            {!institution.approved ? 'Not Approved' : institution.isActive === false ? 'Deactivated' : 'Active'}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }} data-help-section="institutions.detail.section.branding">
                <Typography variant="h6" sx={{ mb: 2 }}>Payslip Branding</Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Shown on the letterhead of generated payslip PDFs.
                </Typography>
                <TextField
                    label="Address"
                    data-help="institutions.detail.field.address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    sx={{ mb: 2 }}
                />
                <TextField
                    label="TIN / BIN"
                    data-help="institutions.detail.field.tinBin"
                    value={tinBin}
                    onChange={(e) => setTinBin(e.target.value)}
                    fullWidth
                    sx={{ mb: 3 }}
                />
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Button variant="outlined" component="label" data-help="institutions.detail.chooseLogo">
                        Upload Logo
                        <input
                            type="file"
                            accept="image/png,image/jpeg"
                            hidden
                            aria-label="upload logo"
                            ref={logoInputRef}
                            onChange={(e) => {
                                const file = e.target.files?.[0] ?? null;
                                if (file && file.size > 1024 * 1024) {
                                    toast.showError('Logo must be 1MB or smaller');
                                    e.target.value = '';
                                    setLogoFile(null);
                                    return;
                                }
                                setLogoFile(file);
                            }}
                        />
                    </Button>
                    {logoFile && <Typography variant="body2">{logoFile.name}</Typography>}
                    <Button
                        variant="contained"
                        onClick={handleUploadLogo}
                        data-help="institutions.detail.uploadLogo"
                        {...helpWhy(!uploadingLogo && !logoFile ? { kind: 'status', message: 'Choose a logo image first with the Upload Logo button.' } : null)}
                        disabled={!logoFile || uploadingLogo}
                    >
                        {uploadingLogo ? 'Uploading...' : 'Upload Logo File'}
                    </Button>
                    <Button
                        variant="text"
                        color="error"
                        onClick={() => setRemoveLogoConfirmOpen(true)}
                        data-help="institutions.detail.removeLogo"
                        disabled={removingLogo}
                    >
                        Remove Logo
                    </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Address and TIN/BIN save with the "Save Settings" button below. The logo uploads immediately.
                </Typography>
            </Paper>

            <Dialog open={removeLogoConfirmOpen} onClose={() => setRemoveLogoConfirmOpen(false)} data-help-page="institutions.detail.removeLogoDialog.page">
                <DialogTitle>Remove Logo</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to remove this institution's logo from payslip letterheads?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRemoveLogoConfirmOpen(false)} disabled={removingLogo} data-help="institutions.detail.removeLogoDialog.cancel">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleRemoveLogo}
                        data-help="institutions.detail.removeLogoDialog.confirm"
                        variant="contained"
                        color="error"
                        disabled={removingLogo}
                    >
                        {removingLogo ? 'Removing...' : 'Confirm Remove'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Paper sx={{ p: 3 }} data-help-section="institutions.detail.section.settings">
                <Typography variant="h6" sx={{ mb: 2 }}>Settings</Typography>
                <Divider sx={{ mb: 2 }} />

                <FormControlLabel
                    data-help="institutions.detail.field.attendanceButtons"
                    control={
                        <Switch
                            checked={isAttendanceButtonEnabled}
                            onChange={(e) => setIsAttendanceButtonEnabled(e.target.checked)}
                        />
                    }
                    label="Enable Check-in / Check-out buttons for employees"
                    sx={{ mb: 3, display: 'block' }}
                />

                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>Weekend days</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Days marked here are treated as weekends in attendance reports and payroll calculations.
                    </Typography>
                    <ToggleButtonGroup
                        value={weekendDays}
                        onChange={(_, next: WeekDay[]) => setWeekendDays(next)}
                        aria-label="weekend days"
                        data-help="institutions.detail.field.weekendDays"
                        size="small"
                    >
                        {WEEK_DAYS.map(d => (
                            <ToggleButton key={d.value} value={d.value} aria-label={d.value}>
                                {d.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>

                <Box sx={{ mb: 3, maxWidth: 360 }}>
                    <FormControl fullWidth size="small" data-help="institutions.detail.field.noShiftPolicy">
                        <InputLabel id="no-shift-policy-label">Attendance when no shift matches</InputLabel>
                        <Select
                            labelId="no-shift-policy-label"
                            label="Attendance when no shift matches"
                            value={noShiftPunchPolicy}
                            onChange={(e) => setNoShiftPunchPolicy(e.target.value as NoShiftPunchPolicy)}
                        >
                            <MenuItem value="LEGACY_ROW">Plain attendance (no shift)</MenuItem>
                            <MenuItem value="DEFAULT_SHIFT">Default 9–5 shift</MenuItem>
                            <MenuItem value="ORPHAN_BLOCK">Block &amp; flag for review</MenuItem>
                        </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Applies only when shift recording is enabled for this institution. "Default 9–5 shift"
                        requires a Default shift to exist.
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveSettings}
                    data-help="institutions.detail.save"
                    disabled={saving}
                >
                    {saving ? 'Saving...' : 'Save Settings'}
                </Button>
            </Paper>
        </Box>
    );
};

export default InstitutionDetail;
